import { getLocalStorageEntry, setLocalStorageEntry } from 'src/utils/webStorageUtils';
import { SOURCE_LANGUAGE } from 'src/utils/language/saveLastLanguagePair';
import { CheckpointCelebrationVideo, getCelebrationVideos } from './celebrationVideos';

export const CELEBRATION_VIDEO_INDEX_PREFIX = 'CELEBRATION-VIDEO-INDEX';

export const getCelebrationVideoIndexStorageKey = (
  sourceLanguageCode: string,
  targetLanguageCode: string,
): string =>
  `${CELEBRATION_VIDEO_INDEX_PREFIX}-${sourceLanguageCode}-${targetLanguageCode}`.toLowerCase();

export const pickCelebrationVideoForCycle = (
  videos: CheckpointCelebrationVideo[],
  cycleIndex: number,
): CheckpointCelebrationVideo | undefined => {
  if (videos.length === 0) {
    return undefined;
  }

  const normalizedIndex = ((cycleIndex % videos.length) + videos.length) % videos.length;
  return videos[normalizedIndex];
};

const parseCycleIndex = (value: string | null): number => {
  if (!value) {
    return 0;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

export const selectAndAdvanceCelebrationVideo = (
  pool: CheckpointCelebrationVideo[],
  storageKey: string,
): CheckpointCelebrationVideo | undefined => {
  if (pool.length === 0) {
    return undefined;
  }

  const cycleIndex = parseCycleIndex(getLocalStorageEntry(storageKey));
  const selected = pickCelebrationVideoForCycle(pool, cycleIndex);
  if (!selected) {
    return undefined;
  }

  setLocalStorageEntry(storageKey, String((cycleIndex + 1) % pool.length));
  return selected;
};

/**
 * Picks the next clip from the shared celebration pool for this language pair
 * and advances the stored index so learn EOS and Quick Review cycle together.
 */
export const selectNextCelebrationVideo = (
  targetLocale?: string | null,
  sourceLocale?: string | null,
): CheckpointCelebrationVideo | undefined => {
  const pool = getCelebrationVideos(targetLocale);
  if (pool.length === 0) {
    return undefined;
  }

  const sourceLanguageCode = sourceLocale ?? getLocalStorageEntry(SOURCE_LANGUAGE);
  const targetLanguageCode = targetLocale?.trim();
  if (!sourceLanguageCode || !targetLanguageCode) {
    return pool[0];
  }

  return selectAndAdvanceCelebrationVideo(
    pool,
    getCelebrationVideoIndexStorageKey(sourceLanguageCode, targetLanguageCode),
  );
};
